import type { ClientLocationPlan } from "api-types/locationPlans/locationPlansResponses";
import type { ClientLocationWithLevel } from "api-types/locations/locationBaseTypes";
import type { ClientMasterPlanHydrated } from "api-types/masterPlans/masterPlansResponses";
import { formatDate } from "common/utils/dates";
import { getLocationIcon } from "lib/components/builders/getLocationIcon";
import { locationPlanLibraryPath } from "lib/routeHelpers";
import type { JSX } from "react";
import { useCallback, useState } from "react";
import Link from "ui/components/Link";
import IconChevronFilled from "ui/icons/IconChevronFilled";
import LocationPlanMenuItems from "./LocationPlanMenuItems";
import styles from "./PlanLibraryLocationsTab.module.scss";

interface NestedLocationsTableProps {
  data: ClientLocationWithLevel[];
  masterPlanName?: string;
  childrenByParent: Map<string, ClientLocationWithLevel[]>;
  builderId?: string;
  planIdByLocationId: Map<string, string>;
  communityPlans: ClientLocationPlan[] | undefined;
  masterPlan: ClientMasterPlanHydrated | undefined;
  onArchivePlan: (locationPlanId: string, locationId: string) => void;
  onUnarchivePlan: (locationPlanId: string, locationId: string) => void;
  showArchived?: boolean;
}

export default function NestedLocationsTable({
  data,
  masterPlanName,
  childrenByParent,
  builderId,
  planIdByLocationId,
  communityPlans,
  masterPlan,
  onArchivePlan,
  onUnarchivePlan,
  showArchived = false,
}: NestedLocationsTableProps): JSX.Element {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (id: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const renderRows = useCallback(
    (nodes: ClientLocationWithLevel[], depth: number): JSX.Element[] => {
      return nodes.flatMap((node) => {
        const children = childrenByParent.get(node.id) ?? [];
        const locationPlanId = planIdByLocationId.get(node.id);
        const communityPlan = communityPlans?.find((cp) => cp.id === locationPlanId);
        const planLabel = `${masterPlanName} at ${node.name}`;
        return [
          <div key={node.id} className={styles.row} data-depth={depth}>
            <div
              className={`${styles.caretCell} ${expanded.has(node.id) ? styles.expanded : ""}`}
              onClick={children.length > 0 ? () => toggle(node.id) : undefined}
              onKeyDown={
                children.length > 0
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggle(node.id);
                      }
                    }
                  : undefined
              }
              role={children.length > 0 ? "button" : undefined}
              tabIndex={children.length > 0 ? 0 : undefined}
              aria-expanded={children.length > 0 ? expanded.has(node.id) : undefined}
              aria-label={
                children.length > 0 ? `${expanded.has(node.id) ? "Collapse" : "Expand"} ${node.name}` : undefined
              }
            >
              {children.length > 0 && <IconChevronFilled />}
            </div>
            <div className={styles.locationCell}>
              <span className={styles.locationIcon}>{getLocationIcon(node.locationLevel?.type)}</span>
              <span className={styles.locationName}>{node.name}</span>
            </div>
            <div className={styles.planNameCell}>
              {builderId && locationPlanId ? (
                <Link
                  href={locationPlanLibraryPath({
                    builderId,
                    locationId: node.id,
                    locationPlanId,
                  })}
                >
                  {planLabel}
                </Link>
              ) : (
                planLabel
              )}
            </div>
            {showArchived && (
              <div className={styles.archivedCell}>
                {communityPlan?.archivedAt ? formatDate(communityPlan.archivedAt, "WITH_TIME") : "--"}
              </div>
            )}
            <div className={styles.actionsCell}>
              {locationPlanId && builderId ? (
                <LocationPlanMenuItems
                  location={node}
                  locationId={node.id}
                  locationPlanId={locationPlanId}
                  communityPlan={communityPlan}
                  masterPlan={masterPlan}
                  builderId={builderId}
                  onArchivePlan={communityPlan?.archivedAt ? null : () => onArchivePlan(locationPlanId, node.id)}
                  onUnarchivePlan={communityPlan?.archivedAt ? () => onUnarchivePlan(locationPlanId, node.id) : null}
                />
              ) : null}
            </div>
          </div>,
          ...(expanded.has(node.id) ? renderRows(children, depth + 1) : []),
        ];
      });
    },
    [
      childrenByParent,
      expanded,
      masterPlanName,
      toggle,
      planIdByLocationId,
      builderId,
      communityPlans,
      masterPlan,
      onArchivePlan,
      onUnarchivePlan,
      showArchived,
    ],
  );

  return (
    <div className={styles.table}>
      <div className={styles.headerRow}>
        <div className={styles.headerSpacer}> </div>
        <div className={styles.location}>Location</div>
        <div className={styles.planName}>Plan Name</div>
        {showArchived && <div className={styles.archived}>Archived</div>}
        <div className={styles.actions}></div>
      </div>
      {renderRows(data, 0)}
    </div>
  );
}
